/**
 * Polygon data model — state management, hit-testing, drag helpers.
 *
 * State: an array of polygon objects and a selected-polygon id.
 * Pure geometry functions are exported for unit testing.
 *
 * Polygon shape:
 *   { id: string, vertices: [{lat, lng}...], fillColor: string, fillColorHex: string, zIndex: number }
 */

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

/** @type {Array<{id: string, vertices: Array<{lat: number, lng: number}>, fillColor: string, fillColorHex: string, zIndex: number}>} */
let polygons = [];

/** @type {string|null} */
let selectedId = null;

// ---------------------------------------------------------------------------
// CRUD exports
// ---------------------------------------------------------------------------

/**
 * Create a new polygon from an array of {lat, lng} vertices, add to state,
 * and return the new polygon object.
 *
 * New polygons are assigned a zIndex one higher than the current maximum,
 * so they appear in front of all existing polygons.
 *
 * @param {Array<{lat: number, lng: number}>} vertices
 * @param {string} [fillColor="rgba(255,255,255,0.35)"] - rgba string for canvas ctx.fillStyle
 * @param {string} [fillColorHex="#ffffff"] - hex string for contrast-adaptive text color
 * @returns {{ id: string, vertices: Array<{lat: number, lng: number}>, fillColor: string, fillColorHex: string, zIndex: number }}
 */
export function addPolygon(vertices, fillColor = "rgba(255,255,255,0.35)", fillColorHex = "#ffffff") {
  const maxZ = polygons.reduce((m, p) => Math.max(m, p.zIndex ?? 0), -1);
  const polygon = {
    id: crypto.randomUUID(),
    vertices: vertices.map((v) => ({ ...v })),
    fillColor,
    fillColorHex,
    zIndex: maxZ + 1,
  };
  polygons.push(polygon);
  return polygon;
}

/**
 * Return a reference to the current polygons array.
 *
 * @returns {Array<{id: string, vertices: Array<{lat: number, lng: number}>, fillColor: string}>}
 */
export function getPolygons() {
  return polygons;
}

/**
 * Return the currently selected polygon id, or null.
 *
 * @returns {string|null}
 */
export function getSelectedId() {
  return selectedId;
}

/**
 * Set the currently selected polygon id.
 *
 * @param {string|null} id
 */
export function setSelectedId(id) {
  selectedId = id;
}

/**
 * Clear all polygons and reset selection. Safe to call when already empty.
 */
export function clearPolygons() {
  polygons = [];
  selectedId = null;
}

/**
 * Replace the polygons array with a deep-copied version of the provided array.
 * Clears selectedId to null.
 *
 * @param {Array<{id: string, vertices: Array<{lat: number, lng: number}>, fillColor: string, fillColorHex: string, zIndex?: number}>} arr
 */
export function setPolygons(arr) {
  polygons = arr.map((p) => ({
    id: p.id,
    vertices: p.vertices.map((v) => ({ ...v })),
    fillColor: p.fillColor,
    fillColorHex: p.fillColorHex,
    zIndex: p.zIndex,
  }));
  selectedId = null;
}

/**
 * Delete the currently selected polygon and clear selection.
 */
export function deleteSelected() {
  if (selectedId === null) return;
  polygons = polygons.filter((p) => p.id !== selectedId);
  selectedId = null;
}

/**
 * Translate every vertex of polygon with given id by (dLat, dLng).
 *
 * @param {string} id
 * @param {number} dLat
 * @param {number} dLng
 */
export function movePolygon(id, dLat, dLng) {
  const polygon = polygons.find((p) => p.id === id);
  if (!polygon) return;
  polygon.vertices = polygon.vertices.map((v) => ({
    lat: v.lat + dLat,
    lng: v.lng + dLng,
  }));
}

// ---------------------------------------------------------------------------
// Pure geometry exports
// ---------------------------------------------------------------------------

/**
 * Point-in-polygon test using the ray-casting even-odd algorithm.
 *
 * @param {number} px - Test point x (canvas pixel)
 * @param {number} py - Test point y (canvas pixel)
 * @param {Array<{x: number, y: number}>} pixelVertices - Polygon vertices in pixel coordinates
 * @returns {boolean}
 */
export function pointInPolygon(px, py, pixelVertices) {
  let inside = false;
  const n = pixelVertices.length;

  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = pixelVertices[i].x;
    const yi = pixelVertices[i].y;
    const xj = pixelVertices[j].x;
    const yj = pixelVertices[j].y;

    const intersects =
      yi > py !== yj > py &&
      px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;

    if (intersects) inside = !inside;
  }

  return inside;
}

/**
 * Clamp a lat/lng drag delta so that no polygon vertex exits the bounding box.
 *
 * @param {Array<{lat: number, lng: number}>} vertices
 * @param {number} dLat
 * @param {number} dLng
 * @param {{ topLat: number, leftLng: number, bottomLat: number, rightLng: number }} bbox
 * @returns {{ dLat: number, dLng: number }}
 */
export function clampDelta(vertices, dLat, dLng, bbox) {
  let clampedDLat = dLat;
  let clampedDLng = dLng;

  for (const v of vertices) {
    const newLat = v.lat + dLat;
    const newLng = v.lng + dLng;

    // Clamp latitude
    if (newLat > bbox.topLat) {
      clampedDLat = Math.min(clampedDLat, bbox.topLat - v.lat);
    }
    if (newLat < bbox.bottomLat) {
      clampedDLat = Math.max(clampedDLat, bbox.bottomLat - v.lat);
    }

    // Clamp longitude
    if (newLng > bbox.rightLng) {
      clampedDLng = Math.min(clampedDLng, bbox.rightLng - v.lng);
    }
    if (newLng < bbox.leftLng) {
      clampedDLng = Math.max(clampedDLng, bbox.leftLng - v.lng);
    }
  }

  return { dLat: clampedDLat, dLng: clampedDLng };
}

/**
 * Convert a canvas pixel delta to a lat/lng delta.
 *
 * Y-axis is flipped: positive dy (moving down) corresponds to decreasing latitude.
 *
 * @param {number} dx - Pixel delta x
 * @param {number} dy - Pixel delta y
 * @param {{ topLat: number, leftLng: number, bottomLat: number, rightLng: number }} bbox
 * @param {{ width: number, height: number }} logicalSize
 * @returns {{ dLat: number, dLng: number }}
 */
export function canvasDeltaToLatLng(dx, dy, bbox, logicalSize) {
  const dLng = (dx / logicalSize.width) * (bbox.rightLng - bbox.leftLng);
  const dLat = -(dy / logicalSize.height) * (bbox.topLat - bbox.bottomLat);
  return { dLat, dLng };
}

/**
 * Check whether a canvas position is close enough to the first vertex of a
 * polygon to trigger the close-polygon gesture.
 *
 * @param {number} canvasX
 * @param {number} canvasY
 * @param {{ x: number, y: number }} firstVertexPixel
 * @param {number} [closeRadius=10] - Proximity threshold in CSS pixels
 * @returns {boolean}
 */
export function isNearFirstVertex(canvasX, canvasY, firstVertexPixel, closeRadius = 10) {
  return (
    Math.hypot(canvasX - firstVertexPixel.x, canvasY - firstVertexPixel.y) <=
    closeRadius
  );
}

// ---------------------------------------------------------------------------
// Vertex editing and self-intersection geometry exports
// ---------------------------------------------------------------------------

/**
 * Cross product of vectors OA and OB, where O is origin, A and B are points.
 * Returns a positive value if O-A-B is counter-clockwise, negative if clockwise,
 * and zero if collinear.
 *
 * @param {{x: number, y: number}} o
 * @param {{x: number, y: number}} a
 * @param {{x: number, y: number}} b
 * @returns {number}
 */
function _cross(o, a, b) {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

/**
 * Test whether two line segments (p1→p2) and (p3→p4) properly intersect.
 *
 * Returns true only for proper crossings (the segments form an X shape).
 * Returns false for:
 *   - Parallel segments
 *   - Segments sharing an endpoint (adjacent polygon edges)
 *   - T-junctions (endpoint touching the interior of the other segment)
 *   - Collinear non-overlapping segments
 *
 * All points are pixel-space {x, y} objects.
 *
 * @param {{x: number, y: number}} p1
 * @param {{x: number, y: number}} p2
 * @param {{x: number, y: number}} p3
 * @param {{x: number, y: number}} p4
 * @returns {boolean}
 */
export function segmentsIntersect(p1, p2, p3, p4) {
  const d1 = _cross(p3, p4, p1);
  const d2 = _cross(p3, p4, p2);
  const d3 = _cross(p1, p2, p3);
  const d4 = _cross(p1, p2, p4);

  // Proper intersection: both pairs of orientations strictly straddle
  if (d1 * d2 < 0 && d3 * d4 < 0) return true;

  return false;
}

/**
 * Check whether a polygon defined by pixel-space vertices is self-intersecting.
 *
 * Returns true if any two non-adjacent edges properly cross, or if the polygon
 * is degenerate (area near zero, all vertices collinear).
 * Returns false for fewer than 3 vertices.
 *
 * @param {Array<{x: number, y: number}>} pixelVerts - Polygon vertices in pixel coordinates
 * @returns {boolean}
 */
export function polygonIsSelfIntersecting(pixelVerts) {
  const n = pixelVerts.length;
  if (n < 3) return false;

  // Check degenerate near-zero area using shoelace formula
  let area = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += pixelVerts[i].x * pixelVerts[j].y;
    area -= pixelVerts[j].x * pixelVerts[i].y;
  }
  if (Math.abs(area) < 1.0) return true;

  // Check all non-adjacent edge pairs
  for (let i = 0; i < n; i++) {
    for (let j = i + 2; j < n; j++) {
      // Skip the pair (i=0, j=n-1) — these are adjacent (share vertex 0)
      if (i === 0 && j === n - 1) continue;

      const p1 = pixelVerts[i];
      const p2 = pixelVerts[(i + 1) % n];
      const p3 = pixelVerts[j];
      const p4 = pixelVerts[(j + 1) % n];

      if (segmentsIntersect(p1, p2, p3, p4)) return true;
    }
  }

  return false;
}

/**
 * Move a single vertex of a polygon to new lat/lng coordinates.
 *
 * Mutates the vertex in place. No-op if polygon id is not found or
 * vertexIndex is out of bounds.
 *
 * @param {string} id - Polygon id
 * @param {number} vertexIndex - Zero-based index of vertex to move
 * @param {number} newLat
 * @param {number} newLng
 */
export function moveVertex(id, vertexIndex, newLat, newLng) {
  const polygon = polygons.find((p) => p.id === id);
  if (!polygon) return;
  if (vertexIndex < 0 || vertexIndex >= polygon.vertices.length) return;
  polygon.vertices[vertexIndex] = { lat: newLat, lng: newLng };
}

/**
 * Insert a new vertex after the given edge index in a polygon's vertex array.
 *
 * The edge at edgeIndex runs from vertices[edgeIndex] to vertices[edgeIndex+1].
 * The new vertex is inserted between them, increasing the vertex count by 1.
 * No-op if polygon id is not found.
 *
 * @param {string} id - Polygon id
 * @param {number} edgeIndex - Index of the edge after whose start vertex the new vertex is inserted
 * @param {{lat: number, lng: number}} vertex - New vertex coordinates
 */
export function insertVertex(id, edgeIndex, vertex) {
  const polygon = polygons.find((p) => p.id === id);
  if (!polygon) return;
  polygon.vertices.splice(edgeIndex + 1, 0, { ...vertex });
}

/**
 * Delete a vertex from a polygon by index.
 *
 * Removes vertices[vertexIndex] via splice.
 * No-op if polygon id is not found or vertexIndex is out of bounds.
 *
 * @param {string} id - Polygon id
 * @param {number} vertexIndex - Zero-based index of vertex to delete
 */
export function deleteVertex(id, vertexIndex) {
  const polygon = polygons.find((p) => p.id === id);
  if (!polygon) return;
  if (vertexIndex < 0 || vertexIndex >= polygon.vertices.length) return;
  polygon.vertices.splice(vertexIndex, 1);
}

/**
 * Compute the minimum distance from point p to the line segment a→b.
 *
 * Uses clamped projection: projects p onto the line through a and b,
 * clamps the parametric parameter t to [0,1], and returns the distance
 * to that clamped point.
 *
 * @param {{x: number, y: number}} p - Query point in pixel space
 * @param {{x: number, y: number}} a - Segment start
 * @param {{x: number, y: number}} b - Segment end
 * @returns {number} Distance in pixels
 */
export function pointToSegmentDistance(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;

  if (lenSq === 0) {
    // Zero-length segment — distance to the point itself
    return Math.hypot(p.x - a.x, p.y - a.y);
  }

  // Parametric projection, clamped to [0, 1]
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq));
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;

  return Math.hypot(p.x - projX, p.y - projY);
}

/**
 * Find the closest edge of a polygon to a given canvas point.
 *
 * Iterates all edges and returns the one with the minimum point-to-segment
 * distance, along with the parametric position along that edge.
 *
 * @param {number} canvasX - Query point x in pixel space
 * @param {number} canvasY - Query point y in pixel space
 * @param {Array<{x: number, y: number}>} pixelVerts - Polygon vertices in pixel coordinates
 * @returns {{ edgeIndex: number, t: number, dist: number }}
 *   edgeIndex: start vertex index of closest edge
 *   t: parametric position along edge (0..1)
 *   dist: distance in pixels
 */
export function closestEdge(canvasX, canvasY, pixelVerts) {
  const p = { x: canvasX, y: canvasY };
  const n = pixelVerts.length;

  let bestEdgeIndex = 0;
  let bestT = 0;
  let bestDist = Infinity;

  for (let i = 0; i < n; i++) {
    const a = pixelVerts[i];
    const b = pixelVerts[(i + 1) % n];

    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lenSq = dx * dx + dy * dy;

    let t = 0;
    if (lenSq > 0) {
      t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq));
    }

    const dist = pointToSegmentDistance(p, a, b);

    if (dist < bestDist) {
      bestDist = dist;
      bestEdgeIndex = i;
      bestT = t;
    }
  }

  return { edgeIndex: bestEdgeIndex, t: bestT, dist: bestDist };
}

// ---------------------------------------------------------------------------
// Z-order exports
// ---------------------------------------------------------------------------

/**
 * Normalize zIndex values to 0-based consecutive integers, preserving
 * relative order. Call after any reorder operation.
 * (Internal — not exported.)
 */
function normalizeZOrder() {
  const sorted = [...polygons].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
  sorted.forEach((p, i) => {
    p.zIndex = i;
  });
}

/**
 * Move polygon with given id to the highest zIndex (front of draw order).
 * No-op if id not found.
 *
 * @param {string} id
 */
export function moveToFront(id) {
  const p = polygons.find((poly) => poly.id === id);
  if (!p) return;
  const maxZ = polygons.reduce((m, poly) => Math.max(m, poly.zIndex ?? 0), 0);
  p.zIndex = maxZ + 1;
  normalizeZOrder();
}

/**
 * Move polygon with given id to the lowest zIndex (back of draw order).
 * No-op if id not found.
 *
 * @param {string} id
 */
export function sendToBack(id) {
  const p = polygons.find((poly) => poly.id === id);
  if (!p) return;
  p.zIndex = -1; // below all others
  normalizeZOrder();
}

/**
 * Increase zIndex of polygon by 1 — swap with the next-higher polygon.
 * No-op if id not found or polygon is already at the top.
 *
 * @param {string} id
 */
export function bringForward(id) {
  const p = polygons.find((poly) => poly.id === id);
  if (!p) return;
  const above = polygons
    .filter((q) => q.id !== id && (q.zIndex ?? 0) > (p.zIndex ?? 0))
    .sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0))[0];
  if (!above) return; // already at top
  const tmp = above.zIndex;
  above.zIndex = p.zIndex;
  p.zIndex = tmp;
  normalizeZOrder();
}

/**
 * Decrease zIndex of polygon by 1 — swap with the next-lower polygon.
 * No-op if id not found or polygon is already at the bottom.
 *
 * @param {string} id
 */
export function sendBackward(id) {
  const p = polygons.find((poly) => poly.id === id);
  if (!p) return;
  const below = polygons
    .filter((q) => q.id !== id && (q.zIndex ?? 0) < (p.zIndex ?? 0))
    .sort((a, b) => (b.zIndex ?? 0) - (a.zIndex ?? 0))[0];
  if (!below) return; // already at bottom
  const tmp = below.zIndex;
  below.zIndex = p.zIndex;
  p.zIndex = tmp;
  normalizeZOrder();
}
